import type { PolicyContext, Section } from './types'

/**
 * Last substantive review of THIS body. Hand-maintained on purpose: a build
 * date would tick on every unrelated deploy and tell a reader the policy was
 * reviewed when nothing was read. Per body, so editing one brand's text does
 * not restamp another brand's.
 */
export const LAST_UPDATED = '23 October 2026'

/**
 * The default policy body, used by every brand without one of its own.
 *
 * TWO CLAIMS WERE REMOVED HERE ON 2026-08-21 AND MUST NOT COME BACK unless the
 * code changes first. `tenant_ip_salt` had NO callers and `ipSalt.rotate()` was
 * dead (the table is emptied and has no writer, #1312, migration 0338), so "a secret unique to your organisation, replaced every 90 days" was
 * false; and nothing pruned the (since emptied, #1312) access log or wrote
 * `share_link_daily_stats` (also emptied, 0338), so "90 days, then only daily totals" promised a
 * deletion that never runs. A security measure you do not have and a deletion
 * you do not perform are the two claims on a privacy page that actually cost
 * something. Guarded in PrivacyPage.test.tsx against BOTH bodies.
 *
 * #1312 (owner 2026-10-23): there is NO share-link access log any more — `share_link_accesses`
 * was emptied and its writer removed (it recorded a Save-to-Drive saver's login email and a bare hash of
 * their user id in the link owner's database, on every link kind, public included). Do not
 * describe an access log, "who opened / saved", or a visitor hash until one exists again.
 *
 * A function rather than a constant so the entity name is woven into the prose
 * instead of appearing as a bare token the reader has to resolve — "we" in a
 * legal document should always be attributable to a named party.
 */
export function buildDefaultSections(
  { productName, company, contactEmail, operatorIsSeparate }: PolicyContext,
): Section[] {
  return [
    {
      id: 'who-we-are',
      heading: 'Who this policy is from',
      body: (
        <>
          <p>
            {operatorIsSeparate
              ? <>{productName} is operated by {company}. </>
              : <>This is the privacy policy for {productName}. </>}
            It explains what personal data we handle when you use {productName}, why we
            handle it, who else sees it, and what you can ask us to do about it.
          </p>
          <p>
            It is written to meet the Personal Data Protection Act 2010 (Malaysia).
          </p>
        </>
      ),
    },
    {
      id: 'two-roles',
      heading: 'Two different roles, and why it matters which one applies',
      body: (
        <>
          <p>
            {productName} is used by organisations. That means we handle personal data
            in two quite different capacities, and your rights differ between them.
          </p>
          <p>
            <strong className="themed-text">Your account.</strong> For the data that
            makes your account work, meaning your name, email address and settings, we
            decide how it is used. Requests about that data come to us.
          </p>
          <p>
            <strong className="themed-text">Your organisation&rsquo;s content.</strong>{' '}
            For files, documents and conversations inside a workspace, the organisation
            that owns the workspace decides what is stored and who may see it. We hold
            and process that content on their instructions. If you want content removed
            or corrected, ask that organisation&rsquo;s administrator first. We will
            direct you to them, because acting on it ourselves would override their
            control of their own records.
          </p>
        </>
      ),
    },
    {
      id: 'what-we-collect',
      heading: 'What we collect',
      body: (
        <>
          <p>
            <strong className="themed-text">Account details.</strong> Your name, email
            address, whether that address is verified, your profile picture if you set
            one, and your preferences. If you sign in with Google, we receive your name,
            email address and profile picture from Google, but never your Google
            password.
          </p>
          <p>
            <strong className="themed-text">Content you put in.</strong> Files you
            upload or create, their contents and earlier versions, folder structure, and
            who you have shared them with.
          </p>
          <p>
            <strong className="themed-text">AI conversations.</strong> What you type to
            the assistant, what it replies, and the files it reads or writes while
            carrying out your request.
          </p>
          <p>
            <strong className="themed-text">Files you attach in AI chat.</strong> When
            you attach a file to an AI chat message, we may record which account
            attached it, the file&rsquo;s size and the time. We use this to show each
            member how much chat storage they are using and to let an organisation&rsquo;s
            administrators set and manage per-member storage allowances.
          </p>
          <p>
            An administrator can see these <strong className="themed-text">figures</strong>:
            how much a member has used, and against what allowance. They do not get
            access to the contents of your chat files through this.
          </p>
          <p>
            Once a conversation has gone 12 months without activity, we remove it along
            with its files and this record.
          </p>
          <p>
            <strong className="themed-text">Usage records.</strong> Measurements used
            for billing and limits: which model ran, how many tokens, how long a task
            took, and which workspace it belonged to.
          </p>
          <p>
            <strong className="themed-text">Shared links.</strong> We do not keep a log of
            who opened a link you shared or who saved a file from it.{' '}
            <strong className="themed-text">
              We do not store IP addresses.
            </strong>{' '}
            If a link asks a visitor for an email address, we use it to confirm they may
            open the link and to identify them while they use it. On a public link we never
            record who a visitor is: what they change is recorded as made through the link.
          </p>
          <p>
            <strong className="themed-text">Payment details.</strong> If your
            organisation pays for {productName}, the payment is handled by Stripe. Card
            numbers go to Stripe and never reach our servers; we keep the invoice
            amounts and status.
          </p>
        </>
      ),
    },
    {
      id: 'why',
      heading: 'Why we handle it',
      body: (
        <>
          <p>To provide {productName}: signing you in, storing your files, running the assistant, and letting you share.</p>
          <p>To keep it secure: detecting abuse, investigating incidents, and enforcing limits.</p>
          <p>To bill for it: measuring usage and issuing invoices.</p>
          <p>To support you: answering questions and fixing faults you report.</p>
          <p>To meet legal obligations, including keeping financial records.</p>
        </>
      ),
    },
    {
      id: 'ai',
      heading: 'What the AI assistant sends elsewhere',
      body: (
        <>
          <p>
            This section is deliberately specific, because it is the question people
            most often want answered.
          </p>
          <p>
            When you use the assistant, your message and whatever file content is needed
            to answer it are sent to the AI provider running the model you selected.
            Depending on the model, that is <strong className="themed-text">Anthropic</strong>,{' '}
            <strong className="themed-text">OpenAI</strong> or{' '}
            <strong className="themed-text">Google</strong>. The model picker names the
            provider before you choose.
          </p>
          <p>
            Your content is sent so the model can respond to you. We do not sell it, and
            we do not use your content to train our own models. Each provider&rsquo;s own
            handling is governed by their terms with us.
          </p>
          <p>
            If you do not want a document&rsquo;s contents leaving our servers, do not ask
            the assistant to work on it. Your organisation&rsquo;s administrator can also
            restrict which models, and therefore which providers, are available.
          </p>
        </>
      ),
    },
    {
      id: 'who-else',
      heading: 'Who else sees your data',
      body: (
        <>
          <p>
            We do not sell personal data. We share it only with the service providers
            below, each doing a specific job for us:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="themed-text">Anthropic, OpenAI, Google</strong>: running the AI models you choose.</li>
            <li><strong className="themed-text">Stripe</strong>: taking payments and storing card details.</li>
            <li><strong className="themed-text">Resend</strong>: delivering email such as sign-in codes and notifications.</li>
            <li><strong className="themed-text">Cloudflare</strong>: serving and protecting the site.</li>
            <li><strong className="themed-text">Hetzner</strong>: hosting the servers your data is stored on.</li>
          </ul>
          <p>
            We may also disclose data where the law requires it, or to establish or
            defend a legal claim.
          </p>
          <p>
            <strong className="themed-text">Data leaving Malaysia.</strong> Several of
            these providers operate outside Malaysia, so your data is transferred abroad
            in the course of providing {productName}. We use providers that commit to
            protecting it to a standard comparable to the PDPA.
          </p>
        </>
      ),
    },
    {
      id: 'cookies',
      heading: 'Cookies',
      body: (
        <>
          <p>
            {productName} sets only the cookies it needs to work, principally the one
            that keeps you signed in.
          </p>
          <p>
            There is no advertising network, no analytics tracker and no third-party
            profiling script on this site. We do not track you across other websites.
          </p>
        </>
      ),
    },
    {
      id: 'retention',
      heading: 'How long we keep it',
      body: (
        <>
          <p>
            <strong className="themed-text">Your account and content:</strong> for as
            long as the account or workspace exists.
          </p>
          <p>
            <strong className="themed-text">Deleted files:</strong> they go to the bin
            and can be restored until your organisation empties it.
          </p>
          <p>
            <strong className="themed-text">Records about a link you shared</strong> (such
            as the email address a visitor proved to open it): kept for as long as the share
            link itself exists, and no longer than we need them. Delete the link and they are
            deleted with it.
          </p>
          <p>
            <strong className="themed-text">Billing and audit records:</strong> kept as
            long as the law requires for financial records, even after an account
            closes. Where we can, we strip the personal details out and keep only the
            amounts.
          </p>
          <p>
            <strong className="themed-text">When you delete your account:</strong> your
            account and your personal workspace, including its files, are deleted, as are
            the access you were given in organisations, your team memberships and any
            requests of yours still pending.
          </p>
          <p>
            What you did inside an organisation&rsquo;s workspace stays with that
            organisation, because it is their record rather than yours to remove: content
            you contributed, access you granted, keys and collections you created, settings
            you changed, and the organisation&rsquo;s activity and audit entries, which can
            include your IP address and device details. These records keep showing your
            email address, marked as a deleted account. The billing and audit entries
            described above stay too.
          </p>
        </>
      ),
    },
    {
      id: 'security',
      heading: 'How we protect it',
      body: (
        <>
          <p>Traffic between you and {productName} is encrypted in transit.</p>
          <p>
            Each organisation&rsquo;s data is isolated at the database level, so one
            organisation&rsquo;s queries cannot reach another&rsquo;s records.
          </p>
          <p>
            Passwords are stored hashed, never in a readable form. Where a log needs to
            tell one person&rsquo;s activity from another&rsquo;s, it holds a one-way hash
            rather than anything that identifies them directly.
          </p>
          <p>
            No system is perfectly secure. If a breach affects your personal data, we
            will tell you and take the steps the law requires.
          </p>
        </>
      ),
    },
    {
      id: 'rights',
      heading: 'Your rights',
      body: (
        <>
          <p>Under the Personal Data Protection Act 2010 you may:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="themed-text">Ask for a copy</strong> of the personal data we hold about you.</li>
            <li><strong className="themed-text">Ask us to correct it</strong> if it is wrong or out of date.</li>
            <li><strong className="themed-text">Withdraw your consent</strong> to our handling it, though this may mean we can no longer provide {productName} to you.</li>
            <li><strong className="themed-text">Tell us to stop</strong> using your data for direct marketing, at any time and without giving a reason.</li>
            <li><strong className="themed-text">Ask us to limit</strong> how we handle it.</li>
          </ul>
          <p>
            {contactEmail
              ? <>Write to <a href={`mailto:${contactEmail}`} className="themed-text hover:underline">{contactEmail}</a> and we will respond within the period the Act allows. We may need to verify who you are first.</>
              : <>Use the contact details below. We will respond within the period the Act allows, and may need to verify who you are first.</>}
          </p>
          <p>
            For content held inside an organisation&rsquo;s workspace, see &ldquo;Two
            different roles&rdquo; above. Those requests go to the organisation.
          </p>
        </>
      ),
    },
    {
      id: 'children',
      heading: 'Children',
      body: (
        <p>
          {productName} is not intended for children under 13, and we do not knowingly
          collect their personal data. If you believe a child has given us personal
          data, contact us and we will delete it.
        </p>
      ),
    },
  ]
}
